import cx from 'classnames';
import React, { useState } from 'react';

import useDarkMode from '../../theme/useDarkMode';
import Button from '../Button';
import Spinner from '../Spinner';

type Color = 'red' | 'green' | 'orange' | 'yellow' | 'blue';
export type ToastType = 'warning' | 'error' | 'success' | 'info';

interface ToastButton {
  label: string;
  href: string;
}

interface ToastProps {
  children?: React.ReactNode;
  className?: string;
  color?: Color;
  type?: ToastType;
  onHide?: () => void;
  canHideSelf?: boolean;
  margin?: string; // optional tailwind class giving the toast a margin
  button?: ToastButton;
}

export const toastBackgroundClasses = {
  critical: 'bg-alert-critical ',
  success: 'bg-alert-success ',
  warning: 'bg-alert-warning ',
  info: 'bg-selected ',
};

export function SuccessToast({ children, ...restProps }: ToastProps) {
  return (
    <Toast color="green" {...restProps}>
      {children}
    </Toast>
  );
}

export function LoadingToast({ children, ...restProps }: ToastProps) {
  return (
    <Toast {...restProps}>
      <span className="mr-3" style={{ position: 'relative', top: '-1px' }}>
        <Spinner size="sm" />
      </span>
      <span>{children}</span>
    </Toast>
  );
}

export function ErrorToast({ children, ...restProps }: ToastProps) {
  return (
    <Toast color="red" {...restProps}>
      {children}
    </Toast>
  );
}

export function WarningToast({ children, ...restProps }: ToastProps) {
  return (
    <Toast color="orange" {...restProps}>
      {children}
    </Toast>
  );
}

export function InfoToast({ children, ...restProps }: ToastProps) {
  return (
    <Toast color="blue" {...restProps}>
      {children}
    </Toast>
  );
}

const getBackgroundClassForColor = (color?: Color) => {
  switch (color) {
    case 'red':
      return toastBackgroundClasses.critical;
    case 'green':
      return toastBackgroundClasses.success;
    case 'blue':
      return toastBackgroundClasses.info;
    case 'orange':
    case 'yellow':
      return toastBackgroundClasses.warning;
  }
};

const getColorForType = (type?: ToastType) => {
  switch (type) {
    case 'error':
      return 'red';
    case 'success':
      return 'green';
    case 'info':
      return 'blue';
    case 'warning':
      return 'orange';
  }
};

type ButtonThemeStyles = {
  normal: React.CSSProperties;
  hover: React.CSSProperties;
};

type ButtonStyleEntry = { light: ButtonThemeStyles; dark: ButtonThemeStyles };

const buttonStyleMap: Record<string, ButtonStyleEntry> = {
  red: {
    light: {
      normal: { backgroundColor: '#F9C6C6' },
      hover: { backgroundColor: '#F5B0B0' },
    },
    dark: {
      normal: { backgroundColor: 'rgba(255, 255, 255, 0.10)', border: 'none' },
      hover: { backgroundColor: 'rgba(255, 255, 255, 0.18)', border: 'none' },
    },
  },
  yellow: {
    light: {
      normal: { backgroundColor: '#F9E68C' },
      hover: { backgroundColor: '#F5DA6A' },
    },
    dark: {
      normal: { backgroundColor: 'rgba(255, 255, 255, 0.10)', border: 'none' },
      hover: { backgroundColor: 'rgba(255, 255, 255, 0.18)', border: 'none' },
    },
  },
  orange: {
    light: {
      normal: { backgroundColor: '#F9E68C' },
      hover: { backgroundColor: '#F5DA6A' },
    },
    dark: {
      normal: { backgroundColor: 'rgba(255, 255, 255, 0.10)', border: 'none' },
      hover: { backgroundColor: 'rgba(255, 255, 255, 0.18)', border: 'none' },
    },
  },
  blue: {
    light: {
      normal: { backgroundColor: '#D9E9FF' },
      hover: { backgroundColor: '#C8DEFF' },
    },
    dark: {
      normal: { backgroundColor: 'rgba(255, 255, 255, 0.10)', border: 'none' },
      hover: { backgroundColor: 'rgba(255, 255, 255, 0.18)', border: 'none' },
    },
  },
};

const buttonVariantMap: Record<string, 'critical' | 'warning' | 'success' | 'primary'> = {
  red: 'critical',
  yellow: 'warning',
  orange: 'warning',
  green: 'success',
  blue: 'primary',
};

function ToastActionButton({
  label,
  href,
  color,
  theme,
}: {
  label: string;
  href: string;
  color: string;
  theme: 'light' | 'dark';
}) {
  const [hovered, setHovered] = useState(false);
  const entry = buttonStyleMap[color];
  const style = entry ? (hovered ? entry[theme].hover : entry[theme].normal) : undefined;

  return (
    <div
      className="ml-4 shrink-0"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Button
        variant={buttonVariantMap[color] ?? 'primary'}
        href={href}
        size="sm"
        className="whitespace-nowrap"
        style={style}
      >
        {label}
      </Button>
    </div>
  );
}

function Toast({
  className,
  children,
  color,
  onHide,
  canHideSelf,
  type,
  margin = 'mb-6',
  button,
}: ToastProps) {
  const { isDarkMode } = useDarkMode();
  const theme = isDarkMode ? 'dark' : 'light';
  const [hidden, setHidden] = useState(false);
  if (hidden) return null;

  const resolvedColor = color ?? getColorForType(type);
  const bgClass = getBackgroundClassForColor(resolvedColor) ?? toastBackgroundClasses.info;

  const classes = cx(
    bgClass,
    'rounded border border-alert py-2 px-4 flex flex-row justify-evenly items-center',
    margin,
    className,
  );

  return (
    <div className={classes} data-testid={`Toast-${type}`}>
      <div className="text-left flex-auto" role="status">
        {children}
      </div>
      {button && resolvedColor && (
        <ToastActionButton label={button.label} href={button.href} color={resolvedColor} theme={theme} />
      )}
      {(onHide || canHideSelf) && (
        <div
          className="ml-4 cursor-pointer text-right flex-auto hover:opacity-75"
          onClick={() => {
            if (onHide) onHide();
            if (canHideSelf) setHidden(true);
          }}
        >
          Hide
        </div>
      )}
    </div>
  );
}

export default React.memo(Toast);
